// #11 SectionTitle, #12 Divider, and the text kinds of the alt grammar: Paragraph (.p, secondary ink; `strong` for the
// primary ink) and Lines (a short column of lines, the first one emphasised). A section title with `count` is the
// list-group heading (H-3): the title and a Count, read after a visually hidden ", " as the navigation badge is, so the
// heading is named "Due, 1", never "Due1".

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Count } from "./Count";

export interface SectionTitleProps {
  /** His heading words (*Breach — dashboard risk*, *Drivers*, *Missing prerequisites* …). */
  children: ReactNode;
  /** The list-group heading: the number of items under it. */
  count?: number | undefined;
  /** What is counted, for assistive technology. */
  countLabel?: string | undefined;
  /** The element: a heading level, or a div (default) when the title is not a document heading. */
  as?: "h2" | "h3" | "h4" | "div" | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function SectionTitle({ children, count, countLabel, as: Tag = "div", id, className }: SectionTitleProps) {
  return (
    <Tag id={id} className={cx("mc-sec-t", count !== undefined && "mc-sec-t-group", className)}>
      {count !== undefined ? <span className="mc-sec-t-w">{children}</span> : children}
      {count !== undefined ? <><VisuallyHidden>, </VisuallyHidden><Count n={count} label={countLabel} /></> : null}
    </Tag>
  );
}

export function Divider({ className }: { className?: string | undefined }) {
  return <hr className={cx("mc-divider", className)} />;
}

export interface ParagraphProps {
  children: ReactNode;
  /** Primary ink (alt .p.t1); default is the secondary ink. */
  strong?: boolean | undefined;
  /** 13.5 px by default; sm is 12.5 px meta text. */
  sm?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function Paragraph({ children, strong, sm, id, className }: ParagraphProps) {
  return <p id={id} className={cx("mc-p", strong && "mc-p-strong", sm && "mc-p-sm", className)}>{children}</p>;
}

export interface LinesProps {
  /** One node per line; the first is emphasised (600, primary ink) unless `plain`. */
  items: readonly ReactNode[];
  plain?: boolean | undefined;
  /** 12 px lines (default 13). */
  sm?: boolean | undefined;
  className?: string | undefined;
}

export function Lines({ items, plain, sm, className }: LinesProps) {
  return (
    <div className={cx("mc-lines", plain && "mc-lines-plain", sm && "mc-lines-sm", className)}>
      {items.map((item, index) => <span key={index}>{item}</span>)}
    </div>
  );
}
